import React, { useState, useContext, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { NationalAccountContext } from '../../../../../contexts';
import {
  modalBlurbs,
  nationalAccountRegex,
  shared,
} from '../../../../../constants/NationalAccount';
import {
  handleMultiLines,
  ReturnError,
  ShowSpinner,
  ToolTip,
  ModalButtonGroup,
} from '../commons';
import { TextField } from '../../../../shared/text-field/text-field';
import { clickCta } from '../../../../../utils/analytics';

export const CodeInputModal = (): JSX.Element => {
  const [agreementCodeNotFoundError, setAgreementCodeNotFoundError] =
    useState(false);
  const [isValidInput, setIsValidInput] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const {
    validResponseState,
    setValidResponseState,
    setShowModal,
    setErrorState,
    errorState,
    fetchValidateAgreementCode,
  } = useContext(NationalAccountContext);
  const {
    register,
    handleSubmit,
    formState: { errors: formErrors },
    setValue,
    reset,
  } = useForm({ mode: 'onSubmit' });

  const validateString = (input: string) => {
    const isInvalidCode =
      !input ||
      input.length === 0 ||
      !input.match(nationalAccountRegex.inputRegex);
    setIsValidInput(!isInvalidCode);
  };

  const handleAgreementCode = ({
    currentTarget,
  }: {
    currentTarget: EventTarget | null;
  }) => {
    const { value } = currentTarget as HTMLInputElement;
    validateString(value.toUpperCase());
  };

  const updateAgreementCodeInput = ({
    currentTarget,
  }: {
    currentTarget: EventTarget | null;
  }) => {
    const { value } = currentTarget as HTMLInputElement;
    const ain = value?.toUpperCase();
    if (!isDirty) {
      setIsDirty(true);
    }
    setAgreementCodeNotFoundError(false);
    validateString(ain);
    setErrorState(false);
    setValue('agreementCode', ain);
  };

  const setValidResponse = useCallback(
    (data) => {
      setValidResponseState({
        requiredEINState: data.einRequired,
        enrollState: false,
        agreementCodeEntered: data.abbreviatedName,
        agreementCodeNameEntered: data.agreementName,
        validatedState: true,
        agreementType: data.agreementType,
        einGovState: data.agreementType === 'GOVERNMENT',
      });
    },
    [validResponseState.enrollState],
  );

  const handleValidateAgreementCode = async (data: any) => {
    const { agreementCode } = data;
    if (agreementCode.length < 1) return;
    setIsLoading(true);
    setErrorState(false);
    const response = await fetchValidateAgreementCode(agreementCode);
    setIsLoading(false);
    if (!response) {
      setErrorState(true);
      setIsValidInput(false);
    } else if (response.hasError) {
      clickCta(
        'pricing benefits: national account incentives overlay: agreement code invalid',
      );
      setAgreementCodeNotFoundError(response?.status === 404);
      setIsValidInput(false);
      setErrorState(response?.status === 500);
      reset();
    } else if (response.data) {
      setValidResponse(response.data);
      setIsValidInput(true);
      clickCta(
        'pricing benefits: national account incentives overlay: agreement code successful validation',
      );
    }
  };

  const cancelValidation = () => {
    setErrorState(false);
    setShowModal(false);
    setAgreementCodeNotFoundError(false);
    clickCta('pricing benefits: national account incentives overlay: cancel');
    setValidResponseState((prevState: any) => {
      return { ...prevState, enrollState: true };
    });
    reset();
  };

  return (
    <div className="sui-flex sui-flex-col sui-justify-center sui-align-start sui-font-regular sui-text-1xl">
      <form onSubmit={handleSubmit(handleValidateAgreementCode)}>
        <div className="sui-pt-4">
          <div className="sui-flex sui-flex-col sui-justify-between">
            <div className="sui-pb-6">
              {handleMultiLines(modalBlurbs.enroll.content)}
            </div>
          </div>
          {isLoading && <ShowSpinner />}
          <div className="sui-pt-1 sui-flex sui-flex-row sui-justify-between sui-align-center">
            <div className="sui-pb-1">{modalBlurbs.enroll.agreementCode}</div>
            <div className="sui-flex sui-flex-row sui-justify-end">
              <ToolTip label={modalBlurbs.enroll.tooltip} />
            </div>
          </div>
        </div>
        <div className="sui-pt-0 sui-pb-4">
          <TextField
            fullWidth
            type="text"
            id="agreementCode"
            autoComplete="off"
            error={
              isDirty &&
              (!isValidInput ||
                (agreementCodeNotFoundError &&
                  !!formErrors.agreementCode?.message))
            }
            helperText={formErrors.agreementCode?.message}
            className="textAreaAgreement"
            onChange={updateAgreementCodeInput}
            onBlur={handleAgreementCode}
            inputProps={{
              ...register('agreementCode', {
                maxLength: 12,
                minLength: 1,
                pattern: nationalAccountRegex.inputRegex,
              }),
            }}
          />
          {isDirty && (agreementCodeNotFoundError || !isValidInput) && (
            <p
              className="sui-text-danger sui-text-xs"
              data-testid="validationErrorText"
            >
              Please enter a valid Agreement Code.
            </p>
          )}
          {errorState && (
            <>
              <div className="sui-h-6" />
              <ReturnError />
            </>
          )}
        </div>
        {isLoading && <ShowSpinner />}
        <ModalButtonGroup
          handleClose={cancelValidation}
          disabled={!isValidInput}
          confirmBlurb={modalBlurbs.enroll.buttons.continue}
          cancelBlurb={shared.cancel}
        />
      </form>
    </div>
  );
};
